/**
 * AppDownloadModal Component
 *
 * Mobile bottom sheet prompting the user to download the app. Only reachable
 * from the mobile menu (the header trigger is `md:hidden`), so the bottom-sheet
 * treatment applies everywhere it can appear.
 *
 * Built on the shared `BottomSheet` pattern (@butter/ui/patterns) — slide-up
 * animation, swipe-to-close, safe-area padding, focus trap, scroll lock — with
 * the store buttons delegated to `AppDownloadButtons` (@butter/ui/domains/app).
 *
 * @module butterUI/components/AppDownloadModal
 */

import { BottomSheet } from "@butter/ui/patterns";
import { ButterLogo } from "@butter/ui/primitives";
import { AppDownloadButtons } from "@butter/ui/domains/app";
import type { AppDownloadModalProps } from "./types";

// Show the debug "device detected" note only in local/staging.
//
// NOTE: this intentionally mirrors `enableInternalTools()` in ~/config/env
// (isDevelopment || isStaging) rather than importing it — that module runs a zod
// parse at import time and throws when required vars are absent, which breaks
// this component's Storybook stories. Keep the two definitions in step.
// Enumerated (rather than `!== "production"`) so an unset var fails closed.
const isInternalEnv =
  import.meta.env.VITE_ENVIRONMENT === "development" ||
  import.meta.env.VITE_ENVIRONMENT === "staging";

export function AppDownloadModal({
  isOpen,
  onClose,
  tenantConfig,
  referrer,
  openAppUrl,
}: AppDownloadModalProps) {
  return (
    <BottomSheet
      open={isOpen}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      ariaLabel="Download the App"
    >
      <div className="flex flex-col items-center gap-4 text-center">
        <ButterLogo variant="icon" />

        <div>
          <h2 className="text-2xl font-bold text-gray-900">Download the App</h2>
          <p className="mt-1 text-gray-600">
            Get the full Butter Golf experience on your mobile device.
          </p>
        </div>

        <AppDownloadButtons
          tenant={tenantConfig}
          referrer={referrer}
          openAppUrl={openAppUrl}
          // Debug "device detected" note only in local/staging, never production.
          showDetectedNote={isInternalEnv}
          className="w-full"
        />
      </div>
    </BottomSheet>
  );
}
